import React, { useCallback, useState } from 'react';
import type { Status } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import AccessibleWorkflowDiagram from '@jira/project-settings__accessible-workflow-diagram/src/WrappedDiagram.tsx';
import { Box, xcss } from '@atlaskit/primitives';
import AkSpinner from '@atlaskit/spinner';
import EmptyState from '@atlaskit/empty-state';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import errorImage from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/error/index.tsx';
import type { WorkflowIdentifier } from './types.tsx';
import { useWorkflowData } from './useWorkflowData.tsx';
import { messages } from './messages.tsx';
import { ZoomControls } from './ZoomControls.tsx';

export type Props = {
	projectId?: string;
	workflowIdentifier: WorkflowIdentifier;
};

const INITIAL_ZOOM = 1;
const ERROR_IMAGE_MAX_HEIGHT = 212;

export const WorkflowViewDiagram = ({ projectId, workflowIdentifier }: Props) => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const [zoom, setZoom] = useState(INITIAL_ZOOM);

	const {
		data: workflow,
		isLoading,
		hasError,
		customErrorMessage,
	} = useWorkflowData(cloudId, workflowIdentifier, projectId);

	const onZoomChange = useCallback((newZoomValue: number) => {
		setZoom(newZoomValue);
	}, []);

	return (
		<>
			<Box xcss={contentStyles}>
				{(() => {
					if (!hasError && isLoading && !workflow) {
						return (
							// Loading state
							<Box xcss={centerWrapperStyles} testId="workflow-view-diagram-modal.loading">
								<AkSpinner size="large" />
							</Box>
						);
					}
					if ((hasError && !isLoading) || !workflow) {
						return (
							// Error state
							<EmptyState
								header={formatMessage(messages.errorMessage)}
								maxImageHeight={ERROR_IMAGE_MAX_HEIGHT}
								renderImage={getAkEmptyStateRenderImageFn(errorImage)}
								description={customErrorMessage || undefined}
							/>
						);
					}

					const statuses = workflow.visibleStatuses.map<Status>(
						({ ref: statusId }) => workflow.allStatuses[statusId],
					);
					return (
						<Box xcss={wrapperStyles}>
							<Box xcss={diagramContentWrapperStyles}>
								<AccessibleWorkflowDiagram
									startNodeLayout={workflow.startNodeLayout}
									statuses={statuses}
									transitions={Object.values(workflow.transitions)}
									zoom={zoom}
									isImmutable
									onZoomUpdate={onZoomChange}
									collapseTransitionLabels
									shouldShowGlobalLoops
								/>
							</Box>
							<ZoomControls zoom={zoom} onZoomChange={onZoomChange} />
						</Box>
					);
				})()}
			</Box>
			<FireScreenAnalytics />
		</>
	);
};

const centerWrapperStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexGrow: 1,
	height: '100%',
});

const wrapperStyles = xcss({
	display: 'flex',
	flexGrow: '1',
	minHeight: '352px',
	boxShadow: 'elevation.shadow.raised',
});

const diagramContentWrapperStyles = xcss({
	width: '100%',
	height: 'auto',
});

const contentStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	flexDirection: 'column',
	height: '100%',
});
